import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  cartSyncRequest = 0;

  /** @type {number | null} */
  optimisticCartCount = null;

  /** @type {number} */
  optimisticCartCountExpiresAt = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value > 0 && value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const rawItemCount = event.detail?.data?.itemCount;
    const itemCount = Number(rawItemCount);

    // Third-party cart integrations can emit the theme event without the
    // itemCount field. Missing data must never be interpreted as an empty cart.
    if (rawItemCount == null || !Number.isFinite(itemCount)) {
      await this.syncCartCount();
      return;
    }

    const comingFromProductForm = event.detail?.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Reconciles the badge with Shopify's authoritative cart state.
   */
  syncCartCount = async () => {
    const request = ++this.cartSyncRequest;
    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return;

      const cart = await response.json();
      const itemCount = Number(cart.item_count);
      if (request !== this.cartSyncRequest || !Number.isFinite(itemCount)) return;

      // A wishlist add is rendered optimistically. Ignore an older cart
      // response while Square's asynchronous request is still completing.
      if (
        this.optimisticCartCount !== null
        && itemCount < this.optimisticCartCount
        && Date.now() < this.optimisticCartCountExpiresAt
      ) return;

      if (
        this.optimisticCartCount === null
        || itemCount >= this.optimisticCartCount
        || Date.now() >= this.optimisticCartCountExpiresAt
      ) {
        this.optimisticCartCount = null;
        this.optimisticCartCountExpiresAt = 0;
      }

      await this.renderCartBubble(itemCount, false, false);
    } catch (_) {
      // Preserve the currently displayed count when cart reconciliation fails.
    }
  };

  /**
   * Immediately reflects an external add-to-cart action while Shopify processes it.
   * @param {number} [quantity=1] - Quantity expected to be added.
   */
  incrementOptimisticCartCount = (quantity = 1) => {
    const visibleCount = Number(this.currentCartCount);
    const startingCount = Number.isFinite(visibleCount) ? visibleCount : 0;
    const nextCount = startingCount + Math.max(1, Number(quantity) || 1);

    this.optimisticCartCount = nextCount;
    this.optimisticCartCountExpiresAt = Date.now() + 5000;
    this.renderCartBubble(nextCount, false);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    const nextCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    const hasItems = nextCount > 0;

    this.currentCartCount = nextCount;
    this.refs.cartBubbleCount.classList.toggle('hidden', !hasItems);
    this.refs.cartBubble.classList.toggle('visually-hidden', !hasItems);
    this.classList.toggle('header-actions__cart-icon--has-cart', hasItems);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || !hasItems) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
